import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import { environment } from '../../environments/environment';
import { AuthService } from './auth.service';
import { SettingsService } from './settings.service';

// Service Models
export interface AirtimeService {
  id: string;
  name: string;
  createdAt: string;
}

export interface DataService {
  id: string;
  name: string;
  createdAt: string;
}

export interface CableService {
  id: string;
  name: string;
  createdAt: string;
}

export interface ElectricityService {
  id: string;
  name: string;
  createdAt: string;
}

// Period Model
export interface BillingPeriod {
  id: number;
  name: string;
}

// Plan Models
export interface DataPlan {
  id: number;
  slug: string;
  name: string;
  amount: number | null;
  billerId: number;
  slugName: string;
}

export interface DiscoPlan {
  id: number;
  slug: string;
  name: string;
  amount: number | null;
  billerId: number;
  billerSlug: string;
  slugName: string;
}

export interface CablePlan {
  id: number;
  slug: string;
  name: string;
  amount: number | null;
  billerId: number;
  billerSlug: string;
  slugName: string;
}

// Validation Models
export interface ValidateElectricityCustomerRequest {
  meterNumber: string;
  billerSlug: string;
  billerId: number;
  packageSlug: string;
}

export interface ValidateElectricityCustomerResponse {
  isValid: boolean;
  message: string;
  customerName: string;
  meterNumber: string;
  minAmount: number;
  customerMessage: string;
}

export interface ValidateCableCustomerRequest {
  smartCardNumber: string;
  billerSlug: string;
  billerId: number;
}

export interface ValidateCableCustomerResponse {
  isValid: boolean;
  message: string;
  customerName: string;
  smartCardNumber: string;
  minAmount: number;
  customerMessage: string;
}

// Purchase Request Models
export interface ProcessAirtimePaymentRequest {
  airtimeServiceId: string;
  amount: number;
  phoneNumber: string;
  transactionPin: string;
  cardId?: string; // Optional: Card ID to use for payment. If null, uses shopping card balance.
}

export interface ProcessDataPaymentRequest {
  dataServiceId: string;
  phoneNumber: string;
  packageSlug: string;
  billerId: number;
  transactionPin: string;
  cardId?: string; // Optional: Card ID to use for payment. If null, uses shopping card balance.
}

export interface ProcessElectricityPaymentRequest {
  electricityServiceId: string;
  amount: number;
  meterNumber: string;
  packageSlug: string;
  billerSlug: string;
  billerId: number;
  transactionPin: string;
  cardId?: string; // Optional: Card ID to use for payment. If null, uses shopping card balance.
}

export interface ProcessCablePaymentRequest {
  cableServiceId: string;
  smartCardNumber: string;
  packageSlug: string;
  billerSlug: string;
  billerId: number;
  transactionPin: string;
  cardId?: string; // Optional: Card ID to use for payment. If null, uses shopping card balance.
}

// Purchase Response Models
export interface ProcessAirtimePaymentResponse {
  success: boolean;
  message: string;
  transactionReference: string;
  paymentReference: string;
  token: string;
  status: string;
  amount: number;
  phoneNumber: string;
  processedAt: string;
}

export interface ProcessDataPaymentResponse {
  success: boolean;
  message: string;
  transactionReference: string;
  paymentReference: string;
  token: string;
  status: string;
  amount: number;
  phoneNumber: string;
  processedAt: string;
}

export interface ProcessElectricityPaymentResponse {
  success: boolean;
  message: string;
  transactionReference: string;
  paymentReference: string;
  token: string;
  status: string;
  amount: number;
  meterNumber: string;
  processedAt: string;
}

export interface ProcessCablePaymentResponse {
  success: boolean;
  message: string;
  transactionReference: string;
  paymentReference: string;
  status: string;
  amount: number;
  smartCardNumber: string;
  processedAt: string;
}

export interface ApiResponse<T> {
  success: boolean;
  message: string;
  data: T;
  errors?: string[];
}

@Injectable({
  providedIn: 'root'
})
export class BillsPaymentService {
  private apiUrl = `${environment.apiUrl}/api/ArkifiHubBillsPayment`;

  constructor(
    private http: HttpClient,
    private authService: AuthService,
    private settingsService: SettingsService
  ) {}

  private getAuthHeaders(): HttpHeaders {
    const token = this.authService.getToken();
    if (token) {
      return new HttpHeaders({
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      });
    }
    return new HttpHeaders({
      'Content-Type': 'application/json'
    });
  }

  private getQueryParams(platformId?: string): HttpParams {
    let params = new HttpParams();
    if (platformId) {
      params = params.set('platformId', platformId);
    }
    const environment = this.settingsService.getEnvironment();
    params = params.set('environment', environment);
    return params;
  }

  // ==========================================
  // Service Lists
  // ==========================================

  getAirtimeServices(platformId?: string): Observable<ApiResponse<AirtimeService[]>> {
    return this.http.get<ApiResponse<AirtimeService[]>>(
      `${this.apiUrl}/services/airtime`,
      {
        headers: this.getAuthHeaders(),
        params: this.getQueryParams(platformId)
      }
    );
  }

  getDataServices(platformId?: string): Observable<ApiResponse<DataService[]>> {
    return this.http.get<ApiResponse<DataService[]>>(
      `${this.apiUrl}/services/data`,
      {
        headers: this.getAuthHeaders(),
        params: this.getQueryParams(platformId)
      }
    );
  }

  getCableServices(platformId?: string): Observable<ApiResponse<CableService[]>> {
    return this.http.get<ApiResponse<CableService[]>>(
      `${this.apiUrl}/services/cable`,
      {
        headers: this.getAuthHeaders(),
        params: this.getQueryParams(platformId)
      }
    );
  }

  getElectricityServices(platformId?: string): Observable<ApiResponse<ElectricityService[]>> {
    return this.http.get<ApiResponse<ElectricityService[]>>(
      `${this.apiUrl}/services/electricity`,
      {
        headers: this.getAuthHeaders(),
        params: this.getQueryParams(platformId)
      }
    );
  }

  // ==========================================
  // Billing Periods
  // ==========================================

  getBillingPeriods(platformId?: string): Observable<ApiResponse<BillingPeriod[]>> {
    return this.http.get<ApiResponse<BillingPeriod[]>>(
      `${this.apiUrl}/periods`,
      {
        headers: this.getAuthHeaders(),
        params: this.getQueryParams(platformId)
      }
    );
  }

  // ==========================================
  // Plans
  // ==========================================

  getDataPlans(serviceName: string, period: string, platformId?: string): Observable<ApiResponse<DataPlan[]>> {
    const request = { serviceName, period };
    return this.http.post<ApiResponse<DataPlan[]>>(
      `${this.apiUrl}/dataplans`,
      request,
      {
        headers: this.getAuthHeaders(),
        params: this.getQueryParams(platformId)
      }
    );
  }

  getDiscoPlans(discoName: string, platformId?: string): Observable<ApiResponse<DiscoPlan[]>> {
    const request = { discoName };
    return this.http.post<ApiResponse<DiscoPlan[]>>(
      `${this.apiUrl}/discoplans`,
      request,
      {
        headers: this.getAuthHeaders(),
        params: this.getQueryParams(platformId)
      }
    );
  }

  getCablePlans(cableTvName: string, platformId?: string): Observable<ApiResponse<CablePlan[]>> {
    const request = { cableTvName };
    return this.http.post<ApiResponse<CablePlan[]>>(
      `${this.apiUrl}/cableplans`,
      request,
      {
        headers: this.getAuthHeaders(),
        params: this.getQueryParams(platformId)
      }
    );
  }

  // ==========================================
  // Validation
  // ==========================================

  validateElectricityCustomer(request: ValidateElectricityCustomerRequest, platformId?: string): Observable<ApiResponse<ValidateElectricityCustomerResponse>> {
    return this.http.post<ApiResponse<ValidateElectricityCustomerResponse>>(
      `${this.apiUrl}/electricity/validate`,
      request,
      {
        headers: this.getAuthHeaders(),
        params: this.getQueryParams(platformId)
      }
    );
  }

  validateCableCustomer(request: ValidateCableCustomerRequest, platformId?: string): Observable<ApiResponse<ValidateCableCustomerResponse>> {
    return this.http.post<ApiResponse<ValidateCableCustomerResponse>>(
      `${this.apiUrl}/cable/validate`,
      request,
      {
        headers: this.getAuthHeaders(),
        params: this.getQueryParams(platformId)
      }
    );
  }

  // ==========================================
  // Purchases
  // ==========================================

  purchaseAirtime(request: ProcessAirtimePaymentRequest, platformId?: string): Observable<ApiResponse<ProcessAirtimePaymentResponse>> {
    return this.http.post<ApiResponse<ProcessAirtimePaymentResponse>>(
      `${this.apiUrl}/airtime/purchase`,
      request,
      {
        headers: this.getAuthHeaders(),
        params: this.getQueryParams(platformId)
      }
    );
  }

  purchaseData(request: ProcessDataPaymentRequest, platformId?: string): Observable<ApiResponse<ProcessDataPaymentResponse>> {
    return this.http.post<ApiResponse<ProcessDataPaymentResponse>>(
      `${this.apiUrl}/data/purchase`,
      request,
      {
        headers: this.getAuthHeaders(),
        params: this.getQueryParams(platformId)
      }
    );
  }

  purchaseElectricity(request: ProcessElectricityPaymentRequest, platformId?: string): Observable<ApiResponse<ProcessElectricityPaymentResponse>> {
    return this.http.post<ApiResponse<ProcessElectricityPaymentResponse>>(
      `${this.apiUrl}/electricity/purchase`,
      request,
      {
        headers: this.getAuthHeaders(),
        params: this.getQueryParams(platformId)
      }
    );
  }

  purchaseCable(request: ProcessCablePaymentRequest, platformId?: string): Observable<ApiResponse<ProcessCablePaymentResponse>> {
    return this.http.post<ApiResponse<ProcessCablePaymentResponse>>(
      `${this.apiUrl}/cable/purchase`,
      request,
      {
        headers: this.getAuthHeaders(),
        params: this.getQueryParams(platformId)
      }
    );
  }

  // ==========================================
  // History
  // ==========================================

  /**
   * Get bills payment history for authenticated customer
   */
  getBillsPaymentHistory(platformId?: string, pageNumber: number = 1, pageSize: number = 20): Observable<ApiResponse<ArkifiHubBillsPaymentHistoryDto>> {
    let params = new HttpParams()
      .set('pageNumber', pageNumber.toString())
      .set('pageSize', pageSize.toString());
    
    if (platformId) {
      params = params.set('platformId', platformId);
    }

    return this.http.get<ApiResponse<ArkifiHubBillsPaymentHistoryDto>>(
      `${this.apiUrl}/history`,
      {
        headers: this.getAuthHeaders(),
        params: params
      }
    );
  }
}

// ==========================================
// History DTOs
// ==========================================

export enum TransactionStatus {
  Pending = 0,
  Successful = 1,
  Failed = 2
}

export interface ArkifiHubBillsPaymentDto {
  id: string;
  platformId: string;
  businessId: string;
  arkifiHubCustomerId: string;
  customerName: string;
  amount: number;
  fee: number;
  totalAmount: number;
  status: TransactionStatus;
  providerReference?: string;
  provider?: string;
  transactionReference: string;
  gateway?: string;
  token?: string;
  service?: string;
  serviceCode?: string;
  environment: number;
  createdAt: string;
  updatedAt: string;
  statusDisplay: string;
  gatewayDisplay: string;
  providerDisplay: string;
  serviceDisplay: string;
}

export interface ArkifiHubBillsPaymentHistoryDto {
  payments: ArkifiHubBillsPaymentDto[];
  totalCount: number;
  pageNumber: number;
  pageSize: number;
  totalPages: number;
}
